import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';

import { isErrorInfo } from '../shared/interfaces/error-info.interface';
import { DashboardPortfolio } from '../shared/ui-models/occupancy-dashboard.ui-model';
import { LoadingData } from '../ui/LoadingData';
import { getOccupancyPortfolio } from './shared/services/data.service';
import { buildRateLadder } from './dashboard/engine';
import { OccupancyDashboard } from './dashboard/OccupancyDashboard';
import { C } from './dashboard/theme';

/**
 * The window the dashboard loads once and then slices in the browser: twelve closed months for the
 * rate ladder and the typical pace shape, the current month, and six months of forward book.
 * Half-open, so `to` is the first day of the month after the last one shown.
 */
const MONTHS_BACK = 12;
const MONTHS_FORWARD = 6;

const loadWindow = () => ({
  from: moment().startOf('month').subtract(MONTHS_BACK, 'months').format('YYYY-MM-DD'),
  to: moment()
    .startOf('month')
    .add(MONTHS_FORWARD + 1, 'months')
    .format('YYYY-MM-DD')
});

export default function OccupancyRateContainer() {
  const [portfolio, setPortfolio] = useState<DashboardPortfolio | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setFailed(false);
    getOccupancyPortfolio(loadWindow()).then((response) => {
      setLoading(false);
      if (isErrorInfo(response)) {
        setFailed(true);
        return;
      }
      setPortfolio(response);
    });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rates = useMemo(() => (portfolio ? buildRateLadder(portfolio) : null), [portfolio]);

  if (loading) return <LoadingData />;

  if (failed || !portfolio || !rates) {
    return (
      <div className="min-h-screen px-6 py-10" style={{ background: C.page }}>
        <div
          className="mx-auto max-w-lg rounded-lg border bg-white p-6 text-center"
          style={{ borderColor: C.line }}
        >
          <p className="text-sm font-semibold" style={{ color: C.ink }}>
            The occupancy data could not be loaded.
          </p>
          <p className="mt-1 text-xs" style={{ color: C.muted }}>
            If this keeps happening, check that your role has access to the occupancy dashboard.
          </p>
          <button
            type="button"
            onClick={load}
            className="mt-4 rounded-md px-6 py-2 text-sm font-medium text-white"
            style={{ background: C.green }}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return <OccupancyDashboard portfolio={portfolio} rates={rates} />;
}
